import React, { useState, useEffect } from 'react';
import { DollarSign, CheckCircle, X, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDateTime } from '../../utils/dateUtils';
import { getPaymentReleaseStatusColor } from '../../utils/statusColors';

interface ReleaseRequest {
  _id: string;
  escrowPaymentId: {
    _id: string;
    amount: number;
    currency: string;
    projectId: {
      projectNumber: string;
      name?: string;
    };
    quoteId: {
      quoteNumber: string;
      title: string;
    };
  };
  requestedBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedAmount: number;
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
}

export const PaymentReleaseDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ReleaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [processing, setProcessing] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/payment-releases/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        let filtered = data.data || [];
        if (filter !== 'all') {
          filtered = filtered.filter((r: ReleaseRequest) => r.status === filter.toUpperCase());
        }
        setRequests(filtered);
      }
    } catch (error) {
      console.error('Fetch release requests error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      setProcessing(requestId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/payment-releases/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchRequests();
      } else {
        alert(data.message || 'Failed to approve release');
      }
    } catch (error) {
      console.error('Approve release error:', error);
      alert('Failed to approve release');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId: string, reason: string) => {
    if (!reason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(requestId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/payment-releases/${requestId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rejectionReason: reason })
      });

      const data = await response.json();
      if (data.success) {
        fetchRequests();
      } else {
        alert(data.message || 'Failed to reject release');
      }
    } catch (error) {
      console.error('Reject release error:', error);
      alert('Failed to reject release');
    } finally {
      setProcessing(null);
    }
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('admin.paymentReleases') || 'Payment Releases'}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('admin.paymentReleasesDescription') || 'Review and approve payment release requests'}
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('admin.all') || 'All'}</option>
          <option value="pending">{t('admin.pending') || 'Pending'}</option>
          <option value="approved">{t('admin.approved') || 'Approved'}</option>
          <option value="rejected">{t('admin.rejected') || 'Rejected'}</option>
        </select>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.noReleaseRequests') || 'No release requests found'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const payment = request.escrowPaymentId as any;
            const project = payment.projectId as any;
            const quote = payment.quoteId as any;

            return (
              <div
                key={request._id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                        <DollarSign className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(request.requestedAmount, payment.currency)}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {project?.projectNumber} - {quote?.quoteNumber}
                        </p>
                      </div>
                    </div>
                    <div className="ml-12 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        <span className="font-medium">{t('admin.requestedBy') || 'Requested by'}:</span>{' '}
                        {request.requestedBy.firstName} {request.requestedBy.lastName} ({request.requestedBy.email})
                      </div>
                      <div>
                        <span className="font-medium">{t('admin.requestedAt') || 'Requested at'}:</span>{' '}
                        {formatDateTime(request.createdAt)}
                      </div>
                      {request.reason && (
                        <div>
                          <span className="font-medium">{t('admin.reason') || 'Reason'}:</span> {request.reason}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {request.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => {
                            const reason = prompt(t('admin.rejectionReason') || 'Rejection reason (optional):');
                            if (reason !== null) {
                              handleReject(request._id, reason);
                            }
                          }}
                          disabled={processing === request._id}
                          className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-md hover:bg-error-50 dark:hover:bg-error-900/20 disabled:opacity-50"
                        >
                          <X className="w-4 h-4 inline mr-1" />
                          {t('admin.reject') || 'Reject'}
                        </button>
                        <button
                          onClick={() => handleApprove(request._id)}
                          disabled={processing === request._id}
                          className="px-4 py-2 bg-success-600 text-white rounded-md hover:bg-success-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {processing === request._id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              {t('admin.processing') || 'Processing...'}
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              {t('admin.approve') || 'Approve'}
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {request.status === 'APPROVED' && (
                      <span className={`px-3 py-1 text-sm rounded font-medium ${getPaymentReleaseStatusColor(request.status)}`}>
                        {t('admin.approved') || 'Approved'}
                      </span>
                    )}
                    {request.status === 'REJECTED' && (
                      <span className={`px-3 py-1 text-sm rounded font-medium ${getPaymentReleaseStatusColor(request.status)}`}>
                        {t('admin.rejected') || 'Rejected'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

